import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  CheckCircleIcon,
  CreditCardIcon,
  ClockIcon,
  CalendarDaysIcon,
  UserIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

const BookServiceConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { service, user } = location.state || {};

  useEffect(() => {
    // Redirect if no service data
    if (!service || !user) {
      navigate('/book-service');
    }
  }, [service, user, navigate]);

  if (!service || !user) {
    return null;
  }

  const getCurrentDate = () => {
    return new Date().toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getServiceEndDate = () => {
    if (service.type === 'pack' && service.duration_days) {
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + service.duration_days);
      return endDate.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
            <CheckCircleIcon className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Paiement réussi !</h1>
          <p className="text-gray-600 mt-2">
            Votre service a été activé avec succès
          </p>
        </div>

        {/* Confirmation Details */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
          {/* Header */}
          <div className="bg-green-50 border-b border-green-200 p-6">
            <h2 className="text-xl font-semibold text-green-800">Détails de votre commande</h2>
            <p className="text-green-600 text-sm mt-1">Commande confirmée le {getCurrentDate()}</p>
          </div>

          {/* Service Details */}
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column - Service Info */}
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CreditCardIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-900">Service acheté</h3>
                    <p className="text-lg font-semibold text-primary-600">{service.name}</p>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full mt-1 ${
                      service.type === 'pack' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-purple-100 text-purple-800'
                    }`}>
                      {service.type === 'pack' ? 'Pack' : 'Abonnement'}
                    </span>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <ClockIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-900">Séances incluses</h3>
                    <p className="text-gray-600">
                      {service.session_count} séances {service.type === 'abonnement' && 'par mois'}
                    </p>
                  </div>
                </div>

                {service.duration_days && (
                  <div className="flex items-start space-x-3">
                    <CalendarDaysIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <h3 className="font-medium text-gray-900">Période de validité</h3>
                      <p className="text-gray-600">
                        Du {getCurrentDate()} au {getServiceEndDate()}
                      </p>
                      <p className="text-sm text-gray-500">({service.duration_days} jours)</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column - Client & Payment Info */}
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <UserIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-900">Client</h3>
                    <p className="text-gray-600">{user.full_name}</p>
                    <p className="text-sm text-gray-500">{user.email}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <CreditCardIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-900">Montant</h3>
                    <p className="text-2xl font-bold text-green-600">{service.price} MAD</p>
                    <p className="text-sm text-gray-500">Commande confirmée</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Service Description */}
            {service.description && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="font-medium text-gray-900 mb-2">Description du service</h3>
                <p className="text-gray-600 text-sm">{service.description}</p>
              </div>
            )}
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-medium text-blue-900 mb-3">Prochaines étapes</h3>
          <div className="space-y-2 text-blue-800">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
              <span className="text-sm">Votre service est maintenant actif et prêt à être utilisé</span>
            </div>
            <div className="flex items-center">
              <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
              <span className="text-sm">Vous pouvez maintenant réserver vos séances dans le planning</span>
            </div>
            <div className="flex items-center">
              <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
              <span className="text-sm">Consultez votre profil pour suivre l'utilisation de vos séances</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => navigate('/')}
            className="flex items-center justify-center px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
          >
            Réserver une séance
            <ArrowRightIcon className="h-5 w-5 ml-2" />
          </button>
          
          <button
            onClick={() => navigate('/profile')}
            className="flex items-center justify-center px-6 py-3 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-lg font-medium transition-colors"
          >
            Voir mon profil
          </button>
          
          <button
            onClick={() => navigate('/')}
            className="flex items-center justify-center px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
          >
            Retour à l'accueil
          </button>
        </div>

        {/* Support Info */}
        <div className="text-center mt-8 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            Besoin d'aide ? Contactez-nous à{' '}
            <a href="mailto:support@nazaistudio.ma" className="text-primary-600 hover:text-primary-700">
              support@nazaistudio.ma
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default BookServiceConfirmation;
